import React, { useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { allProducts } from '../data'; // File ka naam check kar lena (data.js)
import { Zap, Loader2, ArrowRight } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { motion } from 'framer-motion';
import SEO from '../components/SEO'; // 🔥 SEO Import add kar diya

const SearchResults = () => {
    const { isDarkMode } = useTheme();
    const location = useLocation();
    const navigate = useNavigate();
    
    // URL se query nikalna
    const query = new URLSearchParams(location.search).get("query")?.toLowerCase() || "";

    // 🛡️ Safety: Agar data.js khali ho ya load na ho toh crash nahi karega
    const safeProducts = allProducts || [];

    // Filter Logic
    const results = safeProducts.filter(p => 
        p.name?.toLowerCase().includes(query) || 
        p.brand?.toLowerCase().includes(query)
    );

    useEffect(() => {
        // Agar result nahi mila toh seedha Shop page par redirect
        if (query === "" || results.length === 0) {
            const timer = setTimeout(() => {
                navigate('/products'); 
            }, 1500); 
            return () => clearTimeout(timer);
        }
    }, [results.length, query, navigate]);

    // --- CASE 1: AGAR RESULT NA MILE (Redirecting State) ---
    if (query === "" || results.length === 0) {
        return (
            <>
                <SEO 
                    title={`Searching: ${query}...`} 
                    description={`Finding the best matches for ${query} in THE_ARCHIVE.`}
                    url={`/search?query=${query}`}
                />

                <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-black text-white' : 'bg-white text-black'}`}>
                    <div className="text-center space-y-6">
                        <div className="relative inline-block">
                            <Loader2 className="animate-spin text-zinc-500" size={50} strokeWidth={1} />
                            <div className="absolute inset-0 blur-2xl bg-white/10 rounded-full"></div>
                        </div>
                        <h2 className="text-3xl font-black uppercase italic tracking-tighter">Not in Archive.</h2>
                        <p className="text-zinc-500 text-[10px] tracking-[0.4em] uppercase font-bold">Redirecting to Store...</p>
                    </div>
                </div>
            </>
        );
    }

    // --- CASE 2: AGAR RESULT MIL JAYE ---
    return (
        <>
            <SEO 
                title={`Search Results for "${query}" | Archive Finder`} 
                description={`Looking for ${query}? Browse our authenticated thrifted collection to find exactly what you need.`}
                url={`/search?query=${query}`}
            />

            <div className={`min-h-screen pt-32 pb-20 px-6 transition-colors duration-500 ${isDarkMode ? 'bg-[#050505] text-white' : 'bg-white text-black'}`}>
                <div className="max-w-7xl mx-auto">
                    
                    {/* Search Header */}
                    <div className={`border-b pb-12 mb-16 ${isDarkMode ? 'border-white/10' : 'border-black/10'}`}>
                        <p className="text-zinc-500 mb-4 font-black uppercase text-[9px] tracking-[0.5em] flex items-center gap-2">
                            <Zap size={12} className="fill-current text-yellow-500" /> Result_Found: {results.length}
                        </p>
                        <h2 className="text-5xl md:text-7xl font-black italic uppercase tracking-tighter">
                            Matches: <span className={isDarkMode ? 'text-zinc-700' : 'text-zinc-400'}>"{query}"</span>
                        </h2>
                    </div>

                    {/* Products Grid */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
                        {results.map((product, index) => (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.05 }}
                                key={product.id}
                            >
                                <Link to={`/product/${product.id}`} className="group block">
                                    <div className={`aspect-[3/4] overflow-hidden rounded-[2.5rem] mb-6 relative border transition-all duration-500 ${isDarkMode ? 'bg-zinc-900 border-white/5 group-hover:border-white/20' : 'bg-zinc-100 border-black/5 group-hover:border-black/20'}`}>
                                        <img 
                                            src={product.img || product.image} 
                                            className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-110 transition-all duration-700" 
                                            alt={product.name} 
                                            crossOrigin="anonymous"
                                        />
                                        <div className="absolute top-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <div className="bg-white text-black p-3 rounded-full">
                                                <ArrowRight size={16} />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="space-y-1 px-2">
                                        <p className="text-[9px] font-black text-zinc-500 uppercase tracking-[0.3em]">{product.brand}</p>
                                        <h3 className="font-black text-xl uppercase italic tracking-tighter leading-tight group-hover:text-zinc-400 transition-colors">
                                            {product.name}
                                        </h3>
                                        <p className="font-black text-lg tracking-tighter pt-2">₹{product.price}</p>
                                    </div>
                                </Link>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        </>
    );
};

export default SearchResults;